<template>
  <div
    class="editor-side-rail w-44px h-full flex flex-col items-stretch py-2 px-0"
    :style="railThemeVars"
    role="tablist"
    aria-orientation="vertical"
    aria-label="editor side rail"
  >
    <n-tooltip
      v-for="item in visibleRailItems"
      :key="item.key"
      trigger="hover"
      placement="right"
      :show-arrow="false"
      :theme-overrides="tooltipThemeOverrides"
      :content-style="tooltipContentStyle"
    >
      <template #trigger>
        <div
          class="rail-button py-10px"
          :class="{
            'rail-button--active': props.activeKey === item.key,
            'rail-button--disabled': isRailItemDisabled(item.key),
          }"
          role="tab"
          :tabindex="isRailItemDisabled(item.key) ? -1 : 0"
          :aria-label="item.title"
          :aria-selected="props.activeKey === item.key"
          :aria-disabled="isRailItemDisabled(item.key)"
          :data-active="props.activeKey === item.key"
          :data-disabled="isRailItemDisabled(item.key)"
          @click="handleRailItemSelect(item)"
          @keydown.enter.prevent="handleRailItemSelect(item)"
          @keydown.space.prevent="handleRailItemSelect(item)"
        >
          <span class="rail-button-content">
            <span v-if="item.text" class="rail-button-text">{{ item.text }}</span>
            <span v-else class="rail-button-icon" v-html="item.icon" />
          </span>
        </div>
      </template>

      <span>{{ item.title }}</span>
    </n-tooltip>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { CSSProperties } from 'vue';
import { useEditorTooltipTheme } from '@/hooks/useEditorTooltipTheme';
import { useGlobalConfigStore } from '@/stores';

interface SideRailItem {
  key: string;
  title: string;
  icon?: string;
  text?: string;
}

const props = withDefaults(defineProps<{
  activeKey?: string;
  /** 允许展示的侧栏入口；不传时展示完整编辑器侧栏。 */
  visibleKeys?: readonly string[];
  /** 禁止打开的侧栏入口；用于零件模式、只读预览等业务上下文保留入口但阻止展开。 */
  disabledKeys?: readonly string[];
  /** 页面可按需覆盖入口标题，避免复用图标时出现不符合当前语义的 tooltip。 */
  titleOverrides?: Readonly<Record<string, string>>;
}>(), {
  activeKey: '',
});

const emit = defineEmits<{
  select: [key: string];
}>();

const { tooltipThemeOverrides, tooltipContentStyle } = useEditorTooltipTheme({
  padding: '6px 8px',
  lineHeight: '1.4',
});

const globalConfigStore = useGlobalConfigStore();

type EditorSideRailThemeVars = CSSProperties & Record<string, string>;

const isDarkTheme = computed(() => Boolean(globalConfigStore.getProviderTheme()));

const railThemeVars = computed<EditorSideRailThemeVars>(() => {
  if (!isDarkTheme.value) {
    return {
      '--editor-side-rail-bg': '#ffffff',
      '--editor-side-rail-border-color': '#d8e1ec',
      '--editor-side-rail-icon-color': '#334155',
    };
  }

  return {
    '--editor-side-rail-bg': '#0d1117',
    '--editor-side-rail-border-color': '#26313d',
    '--editor-side-rail-icon-color': '#aeb8c5',
  };
});

const disabledRailItemKeys = computed(() => new Set(props.disabledKeys ?? []));

const railItems: SideRailItem[] = [
  {
    key: 'history',
    title: '提交记录',
    icon: `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <g fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="6" cy="5" r="2.2" />
          <circle cx="6" cy="12" r="2.2" />
          <circle cx="16.5" cy="8.5" r="2.2" />
          <path d="M8 5h4.5c1.8 0 3.2 1.4 3.2 3.2v.3" />
          <path d="M8 12h3.7c1.5 0 2.8 1.2 2.8 2.8V19" />
          <path d="M16.5 10.7V19" />
          <path d="M11 19h9" />
        </g>
      </svg>
    `,
  },
  {
    key: 'branch',
    title: '节点与分支',
    icon: `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <g fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="7" cy="5" r="1.8" />
          <circle cx="7" cy="19" r="1.8" />
          <circle cx="17" cy="12" r="1.8" />
          <path d="M7 6.8v10.4" />
          <path d="M8.8 8h3.7c2.5 0 4.5 2 4.5 4.5" />
          <path d="M8.8 16h3.7c2.5 0 4.5-2 4.5-4.5" />
        </g>
      </svg>
    `,
  },
  {
    key: 'comment',
    title: '评论',
    icon: `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M5 6.5h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H11l-4.5 3v-3H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2Z"
        />
      </svg>
    `,
  },
  {
    key: 'note',
    title: '备注',
    icon: `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <g fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M7 4.5h10a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2Z" />
          <path d="M8.5 9.5h7" />
          <path d="M8.5 13h7" />
          <path d="M8.5 16.5h4.5" />
          <path d="M4.5 8.5h2" />
          <path d="M4.5 12h2" />
        </g>
      </svg>
    `,
  },
  {
    key: 'structure',
    title: '结构',
    icon: `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <g fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5.5 8 12 4.5 18.5 8 12 11.5 5.5 8Z" />
          <path d="M5.5 8v7L12 19l6.5-4V8" />
          <path d="M12 11.5V19" />
          <circle cx="18.2" cy="17.8" r="2.1" />
          <path d="M18.2 14.8v1.1" />
          <path d="M18.2 19.7v1.1" />
          <path d="M15.2 17.8h1.1" />
          <path d="M20.1 17.8h1.1" />
        </g>
      </svg>
    `,
  },
  {
    key: 'timer',
    title: '计时器',
    icon: `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <g fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="13" r="6.5" />
          <path d="M9 3.8h6" />
          <path d="M12 6.5v1.5" />
          <path d="M12 13l3-1.7" />
          <path d="M16.8 7.7 18.5 6" />
        </g>
      </svg>
    `,
  },
  {
    key: 'ai',
    title: 'AI 助手',
    text: 'AI',
  },
];

/**
 * 根据页面上下文过滤侧栏入口。
 *
 * 版本 Preview 只允许打开版本历史列表；编辑器仍保留完整入口。把过滤能力收口在
 * 侧栏组件内部，可以避免各页面复制一套图标与键盘交互。
 */
const visibleRailItems = computed(() => {
  const items = props.visibleKeys
    ? railItems.filter((item) => props.visibleKeys.includes(item.key))
    : railItems;

  return items.map((item) => {
    const title = props.titleOverrides?.[item.key];
    return title ? { ...item, title } : item;
  });
});

/**
 * 判断侧栏入口是否处于禁用态。
 *
 * @param key 侧栏入口键。
 * @returns 禁用时返回 true。
 */
function isRailItemDisabled(key: string): boolean {
  return disabledRailItemKeys.value.has(key);
}

/**
 * 统一处理侧栏入口选择。
 *
 * @param item 被用户点击或通过键盘激活的入口。
 */
function handleRailItemSelect(item: SideRailItem): void {
  if (isRailItemDisabled(item.key)) {
    return;
  }
  emit('select', item.key);
}
</script>

<style scoped lang="less">
.editor-side-rail {
  --editor-side-rail-bg: var(--n-color);
  --editor-side-rail-border-color: var(--n-border-color);
  --editor-side-rail-icon-color: var(--n-text-color-2);
  --editor-side-rail-active-color: var(--n-primary-color, var(--puppy-primary));

  box-sizing: border-box;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  flex: 0 0 44px;
  flex-shrink: 0;
  background: var(--editor-side-rail-bg);
  border-right: 1px solid var(--editor-side-rail-border-color);
  color: var(--editor-side-rail-icon-color);
}

.rail-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  outline: none;
  cursor: pointer;
  background-color: transparent;
  color: inherit;
  user-select: none;
}

.rail-button--active {
  --editor-side-rail-icon-color: var(--editor-side-rail-active-color);
}

.rail-button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--editor-side-rail-icon-color);
  transition: color 0.16s ease;
}

.rail-button[aria-selected='true'] .rail-button-content {
  color: var(--editor-side-rail-active-color);
}

.rail-button:hover .rail-button-content,
.rail-button:focus-visible .rail-button-content {
  color: var(--editor-side-rail-active-color);
}

.rail-button--disabled,
.rail-button--disabled:hover,
.rail-button--disabled:focus-visible {
  cursor: not-allowed;
}

.rail-button--disabled[aria-selected='true'] .rail-button-content,
.rail-button--disabled .rail-button-content,
.rail-button--disabled:hover .rail-button-content,
.rail-button--disabled:focus-visible .rail-button-content {
  color: color-mix(in srgb, var(--editor-side-rail-icon-color) 42%, transparent);
}

.rail-button-icon {
  width: 20px;
  height: 20px;
}

.rail-button-text {
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}
</style>
